import { formatDistanceToNow } from "date-fns";
import { Timestamp, deleteDoc, doc } from "firebase/firestore";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { db } from "../firebase/config";
import { useToast } from "./ui/use-toast";

interface BlogCardProps {
  id: string;
  title: string;
  description: string;
  author: string;
  thumbnailUrl: string;
  createdAt: Timestamp;
  onEdit: () => void;
}

const BlogCard = ({
  id,
  title,
  description,
  author,
  thumbnailUrl,
  createdAt,
  onEdit,
}: BlogCardProps) => {
  const { toast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, "BlogsWeb", id));
      toast({
        title: "Success",
        description: "Blog post deleted successfully",
        className: "bg-white text-forest-green border-forest-green",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete blog post. Please try again.",
        className: "bg-white text-red-600 border-red-600",
      });
    }
  };

  return (
    <Card className="group overflow-hidden transition-all hover:shadow-lg hover:ring-1 hover:ring-forest-green/10">
      {/* Thumbnail */}
      <div className="aspect-video w-full overflow-hidden">
        <img
          src={thumbnailUrl}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl text-gray-900 group-hover:text-forest-green line-clamp-2">
            {title}
          </h3>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-forest-green hover:bg-forest-green/5"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-600 hover:bg-red-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-gray-900">
                    Delete Blog Post
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-600">
                    Are you sure you want to delete this blog post? This action
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="text-gray-700 hover:text-gray-900 hover:bg-gray-100">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleDelete}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600 line-clamp-2">{description}</p>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{author}</span>
          </div>
          <time className="text-sm text-gray-500">
            {formatDistanceToNow(createdAt.toDate(), { addSuffix: true })}
          </time>
        </div>
      </div>
    </Card>
  );
};

export default BlogCard;
